import * as htmlToImage from "html-to-image";
import React, { useState, useEffect, useRef, useCallback } from "react";
import DotPulseBackdrop from "../../shared/components/DotPulseBackdrop";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

const fonts = [
  { label: "Dancing Script", style: "'Dancing Script', cursive" },
  { label: "Pacifico", style: "'Pacifico', cursive" },
  { label: "Satisfy", style: "'Satisfy', cursive" },
  { label: "Great Vibes", style: "'Great Vibes', cursive" },
  { label: "Alex Brush", style: "'Alex Brush', cursive" },
  { label: "Allura", style: "'Allura', cursive" },
  { label: "Cookie", style: "'Cookie', cursive" },
  { label: "Kaushan Script", style: "'Kaushan Script', cursive" },
  { label: "Parisienne", style: "'Parisienne', cursive" },
  { label: "Sacramento", style: "'Sacramento', cursive" },
  { label: "Tangerine", style: "'Tangerine', cursive" },
  { label: "Yellowtail", style: "'Yellowtail', cursive" },
  { label: "Zeyada", style: "'Zeyada', cursive" },
  { label: "Cedarville Cursive", style: "'Cedarville Cursive', cursive" },
  { label: "Courgette", style: "'Courgette', cursive" },
  { label: "Herr Von Muellerhoff", style: "'Herr Von Muellerhoff', cursive" },
  { label: "Italianno", style: "'Italianno', cursive" },
  { label: "Marck Script", style: "'Marck Script', cursive" },
  { label: "Rochester", style: "'Rochester', cursive" },
  { label: "Rouge Script", style: "'Rouge Script', cursive" },
];

function ESignatureModal({ formData, setFormData, setOpenSignModal }) {
  const previewRef = useRef(null);
  const modalElementRef = useRef(null);
  const modalContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFont, setSelectedFont] = useState(fonts[0].style);

  const handleHideSignatureModal = () => {
    setOpenSignModal(false);
  };

  const handleClickOutsideModal = useCallback(
    (event) => {
      if (
        modalElementRef.current &&
        !modalElementRef.current.contains(event.target)
      ) {
        handleHideSignatureModal();
      }
    },
    [handleHideSignatureModal]
  );

  useEffect(() => {
    const modalContainer = modalContainerRef.current;
    if (modalContainer) {
      modalContainer.addEventListener("click", handleClickOutsideModal);
    }
    return () => {
      if (modalContainer) {
        modalContainer.removeEventListener("click", handleClickOutsideModal);
      }
    };
  }, [handleClickOutsideModal]);

  const handleUploadSignature = async () => {
    setIsLoading(true);

    setFormData((prevState) => ({
      ...prevState,
      signature_font_family: selectedFont,
    }));

    const scale = 2;
    const node = previewRef.current;
    const rect = node.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const dataUrl = await htmlToImage.toPng(node, {
      width: width * scale,
      height: height * scale,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transformOrigin: "top left",
        transform: `scale(${scale})`,
      },
    });

    setFormData((prevState) => ({
      ...prevState,
      signature: dataUrl,
    }));

    handleHideSignatureModal();
    setIsLoading(false);
  };

  return (
    <>
      {/* Google Fonts */}
      {/* <link
        href="https://fonts.googleapis.com/css2?family=Dancing+Script&family=Great+Vibes&family=Pacifico&family=Satisfy&display=swap"
        rel="stylesheet"
      /> */}
      <link
        href="https://fonts.googleapis.com/css2?family=Alex+Brush&family=Allura&family=Cookie&family=Cedarville+Cursive&family=Courgette&family=Dancing+Script&family=Great+Vibes&family=Herr+Von+Muellerhoff&family=Italianno&family=Kaushan+Script&family=Marck+Script&family=Pacifico&family=Parisienne&family=Rochester&family=Rouge+Script&family=Sacramento&family=Satisfy&family=Tangerine&family=Yellowtail&family=Zeyada&display=swap"
        rel="stylesheet"
      />

      <div
        ref={modalContainerRef}
        className="modal show"
        tabIndex="-1"
        role="dialog"
        style={{
          zIndex: 2,
          display: "block",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          style={{ maxWidth: "720px", margin: "1.75rem auto" }}
        >
          <div
            ref={modalElementRef}
            className="modal-content"
            style={{ backgroundColor: "white" }}
          >
            <div className="modal-header pb-0">
              <h5 className="modal-title">Sign this form</h5>
              <button
                type="button"
                className="header-close-btn"
                onClick={handleHideSignatureModal}
              >
                <span aria-hidden="true" className="close-btn-span">
                  &times;
                </span>
              </button>
            </div>

            <div className="modal-body pt-0">
              <label className="form-label">Type your name</label>
              <input
                maxLength={40}
                value={formData.signature_text}
                className="form-control mb-1"
                onChange={(e) => {
                  setFormData((prevState) => ({
                    ...prevState,
                    signature_text: e.target.value,
                  }));
                }}
              />

              <label className="form-label">Preview:</label>
              <div
                ref={previewRef}
                style={{
                  padding: 16,
                  fontSize: 36,
                  display: "inline-block",
                  fontFamily: selectedFont,
                  backgroundColor: "transparent",
                }}
              >
                {formData.signature_text || "Your Signature Preview"}
              </div>
              <br />
              <FormControl fullWidth className="mb-3">
                <InputLabel id="font-select-label">Choose Style</InputLabel>
                <Select
                  id="font-select"
                  value={selectedFont}
                  label="Choose Style"
                  labelId="font-select-label"
                  sx={{ fontFamily: selectedFont }}
                  onChange={(e) => setSelectedFont(e.target.value)}
                >
                  {fonts.map((font) => (
                    <MenuItem
                      key={font.label}
                      value={font.style}
                      sx={{ fontFamily: font.style }}
                    >
                      {font.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div className="mt-4 d-end">
                <button
                  className="btn btn-secondary me-2"
                  onClick={handleHideSignatureModal}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUploadSignature}
                  disabled={formData.signature_text === ""}
                >
                  Upload Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <DotPulseBackdrop />}
    </>
  );
}

export default ESignatureModal;
